import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import {
  BinaryDependentsFieldModel,
  DynamicFieldModel,
  FormModel,
  MultiDependentsFieldModel,
} from '../model/dynamic-form';
import { associate } from '@ps-gui/shared-utils';
import { createValidator } from '../../input-validation.utils';

export interface DynamicForm {
  formModels: FormModel[];
  formGroup: UntypedFormGroup;
  visibleKeys: string[];
}

@Injectable({
  providedIn: 'root',
})
export class DynamicFormService {
  private readonly formBuilder = inject(FormBuilder);

  private formModels: FormModel[] = [];
  private formGroup!: FormGroup;

  private initialData: Record<string, unknown> = {};
  _formGroups: Record<string, UntypedFormGroup> = {};

  get formGroups() {
    return this._formGroups;
  }

  buildForm(formModels: FormModel[], initialData: Record<string, unknown>): DynamicForm {
    this.formModels = formModels;
    this.initialData = initialData;
    const controls = associate(
      formModels,
      (model) => model.key,
      (model) => this.buildFormGroup(model),
    );
    this.formGroup = this.formBuilder.group({ ...controls });

    Object.entries(this.formGroup.controls).forEach(([key, control]) => {
      this._formGroups[key] = control as UntypedFormGroup;
    });

    return {
      formModels: this.formModels,
      formGroup: this.formGroup,
      visibleKeys: this.getVisibleFormKeys(),
    };
  }

  private buildFormGroup(formModel: FormModel): FormGroup {
    const { type, validators } = formModel;
    let controls;
    if (type === 'binary') {
      controls = this.buildBinaryFormControls(formModel.dependentFieldModel);
    } else if (type === 'multi') {
      controls = this.buildMultiFormControl(formModel.dependentFieldModel);
    } else {
      controls = this.buildFormControls(formModel.fieldModels);
    }

    return this.formBuilder.group(controls, { validators });
  }

  private getVisibleFormKeys() {
    return this.formModels.flatMap((formModel) => {
      if (formModel.type === 'simple') {
        return formModel.fieldModels!.map((fieldModel) => fieldModel.key);
      } else {
        const value = formModel.dependentFieldModel!.value;
        return formModel.dependentFieldModel!.dependents[value ? 'true' : 'false'].map(
          (fieldModel) => fieldModel.key,
        );
      }
    });
  }

  private buildFormControls(fieldModels: DynamicFieldModel[]) {
    return associate(
      fieldModels,
      (fieldModel) => fieldModel.key,
      (fieldModel) => this.createControl(fieldModel),
    );
  }

  private buildBinaryFormControls(binaryDependentsFieldModel: BinaryDependentsFieldModel) {
    const { key, value } = binaryDependentsFieldModel;
    const truthyControls = associate(
      binaryDependentsFieldModel.dependents.true,
      (fieldModel) => fieldModel.key,
      (fieldModel) => this.createControl(fieldModel),
    );
    const falsyControls = associate(
      binaryDependentsFieldModel.dependents.false,
      (fieldModel) => fieldModel.key,
      (fieldModel) => this.createControl(fieldModel),
    );
    const checkboxControl = new FormControl(value ?? false, { nonNullable: true });
    checkboxControl.valueChanges.subscribe((value) => {
      binaryDependentsFieldModel.value = value;
      this.resetControls(value ? Object.values(falsyControls) : Object.values(truthyControls));

      // TODO: create a signal for the current form and update this signal here?
      // this.notifyOnFormModelsChange();
    });
    binaryDependentsFieldModel.control = checkboxControl;
    return {
      [key]: checkboxControl,
      truthyControls: this.formBuilder.group(truthyControls),
      falsyControls: this.formBuilder.group(falsyControls),
    };
  }

  private buildMultiFormControl(multiDependentsFieldModel: MultiDependentsFieldModel) {
    const { key, value } = multiDependentsFieldModel;
    const controls: { [k: string]: { [k: string]: FormControl<unknown> }[] } = {};
    for (const key in multiDependentsFieldModel.dependents) {
      controls[key] = multiDependentsFieldModel.dependents[key].map((fieldModel) => ({
        [fieldModel.key]: this.createControl(fieldModel),
      }));
    }
    const dropdownControl = new FormControl(value, { nonNullable: true });
    dropdownControl.valueChanges.subscribe((value) => {
      multiDependentsFieldModel.value = value;
    });
    multiDependentsFieldModel.control = dropdownControl;
    return {
      [key]: dropdownControl,
      ...controls,
    };
  }

  private createControl(fieldModel: DynamicFieldModel) {
    const initialValue = this.initialData[fieldModel.key] ?? fieldModel.value;
    fieldModel.control = this.formBuilder.nonNullable.control(
      initialValue,
      (fieldModel.validators ?? []).map(createValidator),
    );
    if (fieldModel.disabled) {
      fieldModel.control.disable();
    }
    return fieldModel.control;
  }

  private resetControls(controls: FormControl<unknown>[]) {
    controls.forEach((control) => control.reset());
  }
}
